import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Shield, Gauge, RefreshCw, Search, Wrench, Clock } from 'lucide-react';
import PageTransition from '../../components/ui/PageTransition';
import HeroSection from '../../components/sections/HeroSection';
import SectionHeading from '../../components/ui/SectionHeading';
import CtaSection from '../../components/sections/CtaSection';
import Button from '../../components/ui/Button';

const MaintenancePage = () => {
  return (
    <PageTransition>
      <Helmet>
        <title>Website Maintenance Services | Consiliuma</title>
        <meta name="description" content="Professional website maintenance services in Nottingham. Keep your website secure, up-to-date, and performing at its best with our comprehensive maintenance packages." />
        <meta name="keywords" content="Website Maintenance Nottingham, Website Security, Website Updates, Website Support, Website Performance Optimization" />
        <link rel="canonical" href="/services/maintenance" />
      </Helmet>

      <HeroSection
        title="Website Maintenance Services"
        subtitle="Keep your website secure, fresh, and performing optimally with our comprehensive maintenance packages"
        ctaText="View Maintenance Plans"
        ctaLink="#plans"
        backgroundImage="https://images.pexels.com/photos/1181467/pexels-photo-1181467.jpeg"
      />

      {/* Core Features */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <SectionHeading
            title="Comprehensive Maintenance Solutions"
            subtitle="Everything your website needs to stay secure and up-to-date"
            center
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Shield size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Security Updates</h3>
              <p className="text-secondary">
                Regular security patches, malware scanning, and proactive monitoring to keep your website protected.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Gauge size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Performance Optimization</h3>
              <p className="text-secondary">
                Regular speed optimization, caching configuration, and server performance tuning.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <RefreshCw size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Regular Updates</h3>
              <p className="text-secondary">
                Keeping your CMS, plugins, and themes up-to-date with the latest versions.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Search size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">SEO Monitoring</h3>
              <p className="text-secondary">
                Regular SEO health checks and optimization to maintain search engine rankings.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Wrench size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Technical Support</h3>
              <p className="text-secondary">
                Expert support for any technical issues or questions that arise.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Clock size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Regular Backups</h3>
              <p className="text-secondary">
                Automated daily backups with secure off-site storage for peace of mind.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Maintenance Plans */}
      <section className="py-16 md:py-24 bg-background-alt" id="plans">
        <div className="container-custom">
          <SectionHeading
            title="Maintenance Plans"
            subtitle="Choose the right level of support for your website"
            center
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <motion.div
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold mb-4 text-primary">Essential</h3>
              <p className="text-3xl font-bold mb-6">£99<span className="text-lg text-gray-600">/month</span></p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-primary mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Security monitoring</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-primary mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Weekly backups</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-primary mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Monthly updates</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-primary mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Basic support</span>
                </li>
              </ul>
              <Button to="/quote" variant="outline" className="w-full">
                Get Started
              </Button>
            </motion.div>

            <motion.div
              className="bg-white p-8 rounded-lg shadow-md border-2 border-primary relative"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="absolute top-0 right-0 bg-primary text-white px-4 py-1 rounded-bl-lg text-sm">
                Most Popular
              </div>
              <h3 className="text-2xl font-bold mb-4 text-primary">Professional</h3>
              <p className="text-3xl font-bold mb-6">£199<span className="text-lg text-gray-600">/month</span></p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-primary mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Everything in Essential</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-primary mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Daily backups</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-primary mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Weekly updates</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-primary mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Priority support</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-primary mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Performance optimization</span>
                </li>
              </ul>
              <Button to="/quote" variant="primary" className="w-full">
                Get Started
              </Button>
            </motion.div>

            <motion.div
              className="bg-white p-8 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold mb-4 text-primary">Enterprise</h3>
              <p className="text-3xl font-bold mb-6">£399<span className="text-lg text-gray-600">/month</span></p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-primary mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Everything in Professional</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-primary mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>24/7 emergency support</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-primary mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Monthly SEO reports</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-primary mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Content updates included</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-primary mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Custom development hours</span>
                </li>
              </ul>
              <Button to="/quote" variant="outline" className="w-full">
                Get Started
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <SectionHeading
                title="Why Choose Our Maintenance Service"
                subtitle="Expert care for your digital investment"
              />
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">British Expertise</h3>
                    <p className="text-secondary">
                      Our Nottingham-based team brings years of experience and expertise to every maintenance task.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Proactive Approach</h3>
                    <p className="text-secondary">
                      We identify and fix potential issues before they become problems, ensuring your site stays online.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Comprehensive Service</h3>
                    <p className="text-secondary">
                      From security to performance, we handle all aspects of website maintenance.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <span className="font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Reliable Support</h3>
                    <p className="text-secondary">
                      Quick response times and dedicated support when you need it.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://images.pexels.com/photos/1181244/pexels-photo-1181244.jpeg"
                alt="Website maintenance"
                className="rounded-lg shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection
        title="Keep Your Website in Top Shape"
        subtitle="Let us handle the technical details while you focus on your business"
        buttonText="Get Started"
        buttonLink="/quote"
      />
    </PageTransition>
  );
};

export default MaintenancePage;